import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import BrandMark from "@/components/brand/BrandMark";

/**
 * Shown to anyone signed in without a staff role (1-4): readers (role 5),
 * accounts with zero roles, or accounts with no profile.
 */
const NoStaffAccess = () => {
  const { user, session, authError, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const email = user?.email || session?.user?.email || "This account";

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      navigate("/admin/login", { replace: true });
    }
  };

  return (
    <main className="min-h-screen bg-background flex flex-col">
      <header className="h-16 px-6 flex items-center">
        <BrandMark showAdminTag />
      </header>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <section
          aria-labelledby="no-access-title"
          className="w-full max-w-[480px] rounded-2xl border bg-card p-8 shadow-[0_8px_24px_rgba(21,32,59,0.08)]"
        >
          <div className="h-12 w-12 rounded-full bg-[hsl(var(--rose-50))] text-[hsl(var(--rose))] flex items-center justify-center mb-5">
            <ShieldOff className="h-6 w-6" aria-hidden="true" />
          </div>
          <h1 id="no-access-title" className="font-serif text-[28px] leading-[34px] font-semibold tracking-[-0.3px]">
            This account has no staff access
          </h1>
          <p className="mt-3 text-[15px] leading-6 text-ink-2">
            <span className="font-semibold text-foreground break-all">{email}</span> is signed in, but it
            doesn't have a staff role, so the admin stays closed. Readers use the Hriatna Eng app.
          </p>
          {authError && <p className="mt-3 text-sm text-ink-3">{authError}</p>}
          <p className="mt-3 text-sm text-ink-3">
            If you should have access, ask a Super Admin to give this email a staff role, then sign in again.
          </p>
          <Button onClick={handleSignOut} disabled={signingOut} className="mt-6 h-11 rounded-xl px-5 gap-2">
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {signingOut ? "Signing out…" : "Sign out"}
          </Button>
        </section>
      </div>
    </main>
  );
};

export default NoStaffAccess;
